"use client"

import React, { useEffect, useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { createOrderFromCurrentBasketAction, updateOrderAfterRedirectAction } from "@/actions/basketActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { LoaderCircle } from "lucide-react"
import { useTranslations } from "next-intl"

import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { CheckoutContext } from "@/lib/storefront/checkout/checkout-service"
import { Button } from "@/components/ui/components"
import { UbCheckoutReviewCreateOrderButtonStoryblok } from "@/components/storyblok/component-types"

interface CheckoutReviewCreateOrderButtonProps {
  checkoutContext: CheckoutContext
  blok: UbCheckoutReviewCreateOrderButtonStoryblok
}

export default function CheckoutReviewCreateOrderButtonClient(props: CheckoutReviewCreateOrderButtonProps) {
  const t = useTranslations("storefront")
  const router = useRouter()
  const searchParams = useSearchParams()
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [isPending, setIsPending] = useState(false)
  const isB2c = !props.checkoutContext.isB2B
  const navisionId = props.checkoutContext.customerData?.navisionId
  const [isAdditionalMessageLoaded, setIsAdditionalMessageLoaded] = useState(false)

  const { customOrderId, basketData, messageToMerchant, setMessageToMerchant, setCustomOrderId, setCreatedOrder } =
    useUbStorefrontState()

  useEffect(() => {
    if (!isAdditionalMessageLoaded) {
      if (basketData) {
        if (basketData?.data?.attributes && basketData.data.attributes.length > 0) {
          const basketMessageToMerchant = basketData.data.attributes.find((a) => {
            return "BusinessObjectAttributes#Order_MessageToMerchant" === a.name
          })
          if (basketMessageToMerchant?.value) {
            setMessageToMerchant(basketMessageToMerchant.value as string)
          }

          const basketCustomOrderID = basketData.data.attributes.find((a) => {
            return "CustomOrderID" === a.name
          })
          if (basketCustomOrderID?.value) {
            setCustomOrderId(basketCustomOrderID.value as string)
          }
          setIsAdditionalMessageLoaded(true)
        }
      }
    }
  }, [basketData, isAdditionalMessageLoaded, setCustomOrderId, setMessageToMerchant])

  useEffect(() => {
    const redirect = searchParams.get("redirect")
    const orderId = searchParams.get("orderId")
    if (redirect && orderId) {
      const params: { [key: string]: string } = {}
      searchParams.forEach((value, key) => {
        params[key] = value
      })
      updateOrderAfterRedirectAction(orderId, params).then((value) => {
        if ("success" === redirect) {
          setCreatedOrder({ id: orderId })
          router.push(
            getLinkWithPrefixFromURLAndLocale("/basket/checkout/confirmation", props.checkoutContext.isoLocale)
          )
        } else {
          setErrorMessage(t("order.creation.status.not_completed"))
        }
      })
    }
  }, [searchParams, router, setCreatedOrder, props.checkoutContext.isoLocale, t])

  function createOrder() {
    setIsPending(true)
    createOrderFromCurrentBasketAction(isB2c, customOrderId, navisionId, messageToMerchant).then((value) => {
      if (value?.result === "NOK") {
        console.log(value.message)
        setErrorMessage(t("order.creation.status.not_completed"))
        setIsPending(false)
      } else {
        const createdOrder = value?.orderData
        //console.dir(createdOrder, { depth: 10 })
        setMessageToMerchant("")
        setCustomOrderId("")
        if (createdOrder?.orderCreation?.status === "COMPLETED") {
          setCreatedOrder(createdOrder)
          router.push(
            getLinkWithPrefixFromURLAndLocale("/basket/checkout/confirmation", props.checkoutContext.isoLocale)
          )
        }
      }
    })
  }

  return (
    <>
      {errorMessage && <p className={"text-xs text-red-500"}>{errorMessage}</p>}
      <Button
        onClick={createOrder}
        size={"default"}
        variant={"default"}
        className={"w-full bg-primary py-0 font-normal text-white sm:w-auto"}
        disabled={isPending}
      >
        {!isPending ? (
          <>
            {" "}
            <p>{props.blok.label ?? "Kostenpflichtig bestellen"}</p>
          </>
        ) : (
          <div className={"flex flex-row gap-2"}>
            {" "}
            <p>{props.blok.label_processing ?? "Wird verarbeitet..."}</p>
            <LoaderCircle className={"animate-spin"} />
          </div>
        )}
      </Button>
    </>
  )
}
